import { ApiError } from "@/lib/api";
import type { AuthedProgressUploader, AuthedRequester } from "@/lib/brokerAuth";
import { formatShortDate } from "@/lib/listFormat";

// What a quote is subject to, the notes shared on it and its referral
// (PMS-S749, BRK-S362), and its version history (PMS-S751, declared here and
// not shown yet). Signal's underwriters key the subjectivities in from the
// carrier's letter and close each one; the broker answers one with files and
// shares notes. PMS sends broker-visible notes only.

const BASE = "/api/broker/submissions";

export type SubjectivityTiming = "pre_bind" | "post_bind";
/** Each vocabulary's runtime copy, held to the pin by the contract test. */
export const SUBJECTIVITY_TIMINGS: readonly SubjectivityTiming[] = [
  "pre_bind",
  "post_bind",
];

export type SubjectivityCategory =
  | "loss_runs"
  | "signed_application"
  | "inspection"
  | "payment"
  | "form"
  | "other";
export const SUBJECTIVITY_CATEGORIES: readonly SubjectivityCategory[] = [
  "loss_runs",
  "signed_application",
  "inspection",
  "payment",
  "form",
  "other",
];

export type ResponsibleParty = "broker" | "insured" | "signal" | "carrier";
export const RESPONSIBLE_PARTIES: readonly ResponsibleParty[] = [
  "broker",
  "insured",
  "signal",
  "carrier",
];

/** `open`: waiting on whoever acts. `submitted`: the broker sent documents
 *  and it waits on Signal's review. The rest are closed, by Signal. */
export type SubjectivityStatus =
  | "open"
  | "submitted"
  | "satisfied"
  | "waived"
  | "not_met";
export const SUBJECTIVITY_STATUSES: readonly SubjectivityStatus[] = [
  "open",
  "submitted",
  "satisfied",
  "waived",
  "not_met",
];

export type ReferralStatus =
  | "requested"
  | "approved"
  | "declined"
  | "withdrawn";
export const REFERRAL_STATUSES: readonly ReferralStatus[] = [
  "requested",
  "approved",
  "declined",
  "withdrawn",
];

export interface QuoteSubjectivity {
  id: string;
  description: string;
  timing: SubjectivityTiming;
  category: SubjectivityCategory;
  responsibleParty: ResponsibleParty;
  /** ISO `YYYY-MM-DD`. */
  dueDate: string | null;
  status: SubjectivityStatus;
  /** Why it was waived or not met; null otherwise. */
  statusReason: string | null;
  /** The files that answer it, oldest first; they download through the
   *  insured's documents. */
  documents: { documentId: string; filename: string; uploadedAt: string }[];
}

/** A note shared on the quote: Signal's ("Signal Specialty") or a broker's. */
export interface QuoteNote {
  id: string;
  body: string;
  author: { name: string };
  createdAt: string;
}

export interface QuoteReferral {
  status: ReferralStatus;
  /** When the carrier's underwriter answered; null while requested. */
  decidedAt: string | null;
}

/** One version of the quote, and what changed from its basis (PMS-S751). */
export interface QuoteVersionSummary {
  version: number;
  kind: "indication" | "quote";
  bindStatus: "quotable" | "refer" | "decline" | null;
  status?: "current" | "replaced" | "not_applied";
  totalPremium: string;
  taxesAndFees: string;
  totalCost: string;
  createdAt: string;
  changes: QuoteChange[];
}

export interface QuoteChange {
  /** `request`: what the broker changed. `offer`: what the carrier did. */
  section: "request" | "offer";
  field: string;
  label: string;
  format:
    | "money"
    | "date"
    | "number"
    | "percent"
    | "factor"
    | "per_100"
    | "boolean"
    | "enum"
    | "state"
    | "text";
  group?: string;
  from: string | number | boolean | string[] | null;
  to: string | number | boolean | string[] | null;
}

/** The longest note PMS takes. */
export const NOTE_MAX = 4000;

/** Who acts on a subjectivity, in a word. */
export const PARTY_LABELS: Record<ResponsibleParty, string> = {
  broker: "Broker",
  insured: "Insured",
  signal: "Signal",
  carrier: "Carrier",
};

/** A subjectivity's status, as its chip says it. */
export const SUBJECTIVITY_STATUS_LABELS: Record<SubjectivityStatus, string> = {
  open: "Open",
  submitted: "Submitted",
  satisfied: "Satisfied",
  waived: "Waived",
  not_met: "Not met",
};

/** Before binding, then after, each in PMS's order (oldest first). */
export function subjectivityGroups(
  subjectivities: readonly QuoteSubjectivity[] | undefined,
): { beforeBinding: QuoteSubjectivity[]; afterBinding: QuoteSubjectivity[] } {
  const all = subjectivities ?? [];
  return {
    beforeBinding: all.filter((s) => s.timing === "pre_bind"),
    afterBinding: all.filter((s) => s.timing === "post_bind"),
  };
}

/** Still open and past its date (`today` as ISO `YYYY-MM-DD`). */
export function isOverdue(
  subjectivity: QuoteSubjectivity,
  today: string,
): boolean {
  return (
    subjectivity.status === "open" &&
    subjectivity.dueDate !== null &&
    subjectivity.dueDate < today
  );
}

/** Whether the broker can answer it with files: the broker or the insured
 *  acts on it, and Signal hasn't closed it. */
export function answerable(subjectivity: QuoteSubjectivity): boolean {
  return (
    (subjectivity.status === "open" || subjectivity.status === "submitted") &&
    (subjectivity.responsibleParty === "broker" ||
      subjectivity.responsibleParty === "insured")
  );
}

/** How many before-binding subjectivities Signal hasn't closed yet. */
export function openBeforeBinding(
  subjectivities: readonly QuoteSubjectivity[] | undefined,
): number {
  return subjectivityGroups(subjectivities).beforeBinding.filter(
    (s) => s.status === "open" || s.status === "submitted",
  ).length;
}

/** Where Signal's request to the carrier's underwriter stands: a chip that
 *  replaces the referral's when it was decided, and the sentence the Status
 *  says. Null when there's none (or it was withdrawn): BRK-S354's own words
 *  stand. */
export function referralStanding(
  referral: QuoteReferral | null | undefined,
): { chip: string | null; line: string } | null {
  if (!referral) return null;
  const on = referral.decidedAt
    ? ` on ${formatShortDate(referral.decidedAt)}`
    : "";
  switch (referral.status) {
    case "requested":
      return {
        chip: null,
        line: "Signal has sent it to the carrier’s underwriter for review.",
      };
    case "approved":
      return {
        chip: "Underwriter approved",
        line: `The carrier’s underwriter approved it${on}.`,
      };
    case "declined":
      return {
        chip: "Declined by underwriter",
        line: `The carrier’s underwriter declined it${on}.`,
      };
    case "withdrawn":
      return null;
  }
}

/** Why a note can't be sent, as PMS would refuse it; null when it can. */
export function noteError(body: string): string | null {
  const trimmed = body.trim();
  if (trimmed === "") return "Write the note first.";
  if (trimmed.length > NOTE_MAX) {
    return `Keep a note to ${NOTE_MAX.toLocaleString("en-US")} characters or fewer.`;
  }
  return null;
}

const quotePath = (submissionId: string, quoteId: string) =>
  `${BASE}/${encodeURIComponent(submissionId)}/quotes/${encodeURIComponent(quoteId)}`;

/** POST …/quotes/{quoteId}/notes: a note shared with Signal, never edited.
 *  A 201 without the note isn't read as added (NN #7). */
export async function addQuoteNote(
  request: AuthedRequester,
  submissionId: string,
  quoteId: string,
  body: string,
): Promise<QuoteNote> {
  const answer = await request<{ note?: QuoteNote }>(
    `${quotePath(submissionId, quoteId)}/notes`,
    { method: "POST", body: { body: body.trim() } },
  );
  if (!answer.note) {
    throw new ApiError(
      201,
      "The note was taken but nothing came back to show — reload to see it.",
    );
  }
  return answer.note;
}

/** POST …/subjectivities/{subjectivityId}/documents: one file a request,
 *  with no `type` (the subjectivity's category names it), so each file's
 *  progress, Stop and refusal are its own. Answers the subjectivity as PMS
 *  now holds it. */
export async function answerSubjectivity(
  upload: AuthedProgressUploader,
  at: { submissionId: string; quoteId: string; subjectivityId: string },
  input: {
    file: File;
    onProgress?: (progress: { loaded: number; total: number }) => void;
    signal?: AbortSignal;
  },
): Promise<QuoteSubjectivity> {
  const form = new FormData();
  form.append("files", input.file);
  const answer = await upload<{ subjectivity?: QuoteSubjectivity }>(
    `${quotePath(at.submissionId, at.quoteId)}/subjectivities/${encodeURIComponent(at.subjectivityId)}/documents`,
    form,
    { onProgress: input.onProgress, signal: input.signal },
  );
  if (!answer.subjectivity) {
    throw new ApiError(
      201,
      "That file was accepted but nothing came back to show — reload to check.",
    );
  }
  return answer.subjectivity;
}
